import type { DataFlowBuilder } from '@/engine/DataFlow';
import * as DT from './CardiacDataTypes';
import * as Algo from './CardiacAlgorithmHelpers';
import type { BuildInfo } from '@/engine/DataFlow/DataFlowBuilder';

export function buildPointToPlaneDistanceMeasurement(
  builder: DataFlowBuilder,
  pointDescriptor: string,
  planeDescriptor: string
): { distance: BuildInfo; line: BuildInfo } {
  const pt = builder.existing(pointDescriptor);
  const proj = builder.bindRaw(Algo.projectPointOnPlane, DT.Point, pt, builder.existing(planeDescriptor));
  const line = builder.bindRaw(Algo.lineBetweenPoints, DT.Line, pt, proj);
  const distance = builder.bindRaw(Algo.lengthOfTheLine, DT.NumberT, line);
  return {
    distance: distance,
    line: line
  };
}

export function buildPointToPlaneDistanceMeasurementSigned(
  builder: DataFlowBuilder,
  pointDescriptor: string,
  planeDescriptor: string
): { distance: BuildInfo; line: BuildInfo } {
  const pt = builder.existing(pointDescriptor);
  const pl = builder.existing(planeDescriptor);
  const proj = builder.bindRaw(Algo.projectPointOnPlane, DT.Point, pt, pl);
  const line = builder.bindRaw(Algo.lineBetweenPoints, DT.Line, pt, proj);
  const distance = builder.bindRaw(Algo.distancePointToPlane, DT.NumberT, pt, pl);
  return {
    distance: distance,
    line: line
  };
}

export function buildPointToPlaneDistanceMeasurementSignedInv(
  builder: DataFlowBuilder,
  pointDescriptor: string,
  planeDescriptor: string
): { distance: BuildInfo; line: BuildInfo } {
  const info = buildPointToPlaneDistanceMeasurementSigned(builder, pointDescriptor, planeDescriptor);
  const distance = builder.bindRaw(Algo.negateNumber, DT.NumberT, info.distance);
  return {
    distance: distance,
    line: info.line
  };
}

export function buildPointToPointDistanceMeasurement(
  builder: DataFlowBuilder,
  point1Descriptor: string,
  point2Descriptor: string
): { distance: BuildInfo; line: BuildInfo } {
  const line = builder.bindRaw(
    Algo.lineBetweenPoints,
    DT.Line,
    builder.existing(point1Descriptor),
    builder.existing(point2Descriptor)
  );

  const distance = builder.bindRaw(Algo.lengthOfTheLine, DT.NumberT, line);

  return {
    distance: distance,
    line: line
  };
}

export function buildPointToPointDistanceMeasurementSigned(
  builder: DataFlowBuilder,
  point1Descriptor: string,
  point2Descriptor: string,
  directionDescriptor: string
): { distance: BuildInfo; line: BuildInfo } {
  const line = builder.bindRaw(
    Algo.lineBetweenPoints,
    DT.Line,
    builder.existing(point1Descriptor),
    builder.existing(point2Descriptor)
  );

  const distance = builder.bindRaw(Algo.lengthOfTheLineSigned, DT.NumberT, line, builder.existing(directionDescriptor));

  return {
    distance: distance,
    line: line
  };
}

export function buildAreaMeasurement(
  builder: DataFlowBuilder,
  planeDescriptor: string,
  trianglesDescriptor: string,
  zVectorDescriptor: string,
  yVectorDescriptor?: string,
  shiftLimitPointDescriptor?: string
): void {
  if (shiftLimitPointDescriptor) {
    builder
      .name(planeDescriptor + '.shift_range')
      .bindRaw(
        Algo.planeShiftRangeLimited,
        DT.Line,
        builder.existing(planeDescriptor),
        builder.existing(shiftLimitPointDescriptor),
        builder.define(new DT.NumberT(10))
      );
  } else {
    builder
      .name(planeDescriptor + '.shift_range')
      .bindRaw(Algo.planeShiftRange, DT.Line, builder.existing(planeDescriptor), builder.define(new DT.NumberT(10)));
  }
  builder
    .name(planeDescriptor + '.area.pts')
    .bindRaw(
      Algo.tuneValue,
      DT.Polyline,
      builder
        .name(planeDescriptor + '.area.pts.dflt')
        .bind(Algo.sliceAortaWithPlane, DT.Polyline, trianglesDescriptor, planeDescriptor),
      builder.name(planeDescriptor + '.area.pts.user').define(Algo.emptyPolyline()),
      builder.name(planeDescriptor + '.area.pts.appr').define(Algo.emptyPolyline()),
      builder.name(planeDescriptor + '.area.pts.flag').define(Algo.defaultTuneFlag())
    );
  if (yVectorDescriptor) {
    builder
      .name(planeDescriptor + '.area')
      .bind(
        Algo.areaMeasurement,
        DT.AreaMeasurement,
        planeDescriptor + '.area.pts',
        zVectorDescriptor,
        yVectorDescriptor
      );
  } else {
    builder
      .name(planeDescriptor + '.area')
      .bind(Algo.areaMeasurement, DT.AreaMeasurement, planeDescriptor + '.area.pts', zVectorDescriptor);
  }
}

export function buildDeliveryAlgorithm(
  builder: DataFlowBuilder,
  minCatheter = 14,
  maxCatheter = 24,
  defCatheter = 18
): void {
  builder.name('vessel.descriptor').define(Algo.emptyMeshDescriptor());
  builder.name('vessel_calcification.descriptor').define(Algo.emptyMeshDescriptor());

  builder.name('vessel').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'vessel.descriptor');
  builder.name('vessel_calcification').bind(Algo.loadDracoMeshAsync, DT.Mesh, 'vessel_calcification.descriptor');

  builder.name('pt_right_femoral_vessel_imported').define(Algo.emptyPoint());
  builder.name('pt_left_femoral_vessel_imported').define(Algo.emptyPoint());
  builder.name('pt_centerline_reference_imported').define(Algo.emptyPoint());

  builder.name('isAccessPlanningAvailable').bind(Algo.isMeshNotEmpty, DT.BooleanT, 'vessel');

  builder
    .name('tp.pt_right_femoral_vessel')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_right_femoral_vessel.dflt')
        .bind(Algo.reference, DT.Point, 'pt_right_femoral_vessel_imported'),
      builder.name('tp.pt_right_femoral_vessel.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_right_femoral_vessel.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_right_femoral_vessel.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_left_femoral_vessel')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_left_femoral_vessel.dflt').bind(Algo.reference, DT.Point, 'pt_left_femoral_vessel_imported'),
      builder.name('tp.pt_left_femoral_vessel.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_left_femoral_vessel.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_left_femoral_vessel.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name('tp.pt_centerline_reference')
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder
        .name('tp.pt_centerline_reference.dflt')
        .bind(Algo.reference, DT.Point, 'pt_centerline_reference_imported'),
      builder.name('tp.pt_centerline_reference.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_centerline_reference.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_centerline_reference.flag').define(Algo.defaultTuneFlag())
    );

  builder.name('vessel.triangles').bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'vessel');

  builder
    .name('vessel.centerline_tree')
    .bindRaw(
      Algo.calculateCenterline,
      DT.MatPolylinesVector,
      builder.existing('vessel.triangles'),
      builder.define(new DT.NumberT(0.75))
    );
  builder
    .name('vessel.centerline')
    .bind(Algo.lineSegmentsMeshFromMatPolylinesVector, DT.LineSegmentsMesh, 'vessel.centerline_tree');

  builder
    .name('vessel.right_sharp_centerline')
    .bind(
      Algo.shortestPathOverPolylines,
      DT.MatPolyline,
      'vessel.centerline_tree',
      'tp.pt_right_femoral_vessel',
      'tp.pt_centerline_reference'
    );
  builder
    .name('vessel.right_smooth_centerline')
    .bindRaw(
      Algo.smoothenPolyline,
      DT.MatPolyline,
      builder.existing('vessel.right_sharp_centerline'),
      builder.define(new DT.NumberT(1)),
      builder.define(new DT.NumberT(0.8))
    );
  builder
    .name('vessel.right_centerline.points')
    .bind(Algo.polylineFromMatPolyline, DT.Polyline, 'vessel.right_smooth_centerline');
  builder.name('vessel.right_centerline').bind(Algo.makeCenterline, DT.Centerline, 'vessel.right_centerline.points');

  builder
    .name('vessel.left_sharp_centerline')
    .bind(
      Algo.shortestPathOverPolylines,
      DT.MatPolyline,
      'vessel.centerline_tree',
      'tp.pt_left_femoral_vessel',
      'tp.pt_centerline_reference'
    );
  builder
    .name('vessel.left_smooth_centerline')
    .bindRaw(
      Algo.smoothenPolyline,
      DT.MatPolyline,
      builder.existing('vessel.left_sharp_centerline'),
      builder.define(new DT.NumberT(1)),
      builder.define(new DT.NumberT(0.8))
    );
  builder
    .name('vessel.left_centerline.points')
    .bind(Algo.polylineFromMatPolyline, DT.Polyline, 'vessel.left_smooth_centerline');
  builder.name('vessel.left_centerline').bind(Algo.makeCenterline, DT.Centerline, 'vessel.left_centerline.points');

  builder
    .name('vessel_calcification.triangles')
    .bind(Algo.triangleSurfaceFromMesh, DT.MatTriangleSurface, 'vessel_calcification');
  builder
    .name('vessel_wo_vessel_calcification.triangles')
    .bind(Algo.subtractSurfaces, DT.MatTriangleSurface, 'vessel.triangles', 'vessel_calcification.triangles');

  builder
    .name('tp.catheterSize')
    .bindRaw(
      Algo.tuneValue,
      DT.NumberT,
      builder.name('tp.catheterSize.dflt').define(new DT.NumberT(defCatheter)),
      builder.name('tp.catheterSize.user').define(new DT.NumberT(0)),
      builder.name('tp.catheterSize.appr').define(new DT.NumberT(0)),
      builder.name('tp.catheterSize.flag').define(Algo.defaultTuneFlag())
    );
  builder.name('tp.catheterSize.range').define(new DT.RangeT([minCatheter, maxCatheter]));

  builder.name('catheter_allowance_factor_range').define(new DT.RangeT([0.75, 1.25]));

  builder
    .name('vessel.combined_centerline')
    .bind(
      Algo.combineMatPolylines,
      DT.MatPolylinesVector,
      'vessel.right_smooth_centerline',
      'vessel.left_smooth_centerline'
    );
  builder
    .name('vessel.smooth_centerline')
    .bind(Algo.mergeMatPolylinesVector, DT.MatLineSegments, 'vessel.combined_centerline');
  builder
    .name('vessel.smooth_centerline.edge_tree')
    .bind(Algo.buildEdgeTree, DT.MatKDTree3d, 'vessel.smooth_centerline');
  builder
    .name('vessel.smooth_centerline.vertex_diameters')
    .bind(
      Algo.calculateDiameters,
      DT.MatDoublesVector,
      'vessel_wo_vessel_calcification.triangles',
      'vessel.smooth_centerline'
    );

  builder.name('vessel.colored').bind(Algo.meshFromTriangleSurface, DT.Mesh, 'vessel.triangles');

  builder
    .name('vessel.colored.colors')
    .bind(
      Algo.centerlineDiametersVertexColorMask,
      DT.ArrayT,
      'vessel.triangles',
      'vessel.smooth_centerline',
      'vessel.smooth_centerline.edge_tree',
      'vessel.smooth_centerline.vertex_diameters',
      'tp.catheterSize',
      'catheter_allowance_factor_range'
    );

  builder
    .name('tp.pl_right_femoral_vessel_min')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder
        .name('tp.pl_right_femoral_vessel_min.dflt')
        .bind(
          Algo.minSlicePlaneAlongPolyline,
          DT.Plane,
          'vessel.smooth_centerline',
          'vessel.smooth_centerline.edge_tree',
          'vessel.smooth_centerline.vertex_diameters',
          'vessel.right_smooth_centerline'
        ),
      builder.name('tp.pl_right_femoral_vessel_min.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_right_femoral_vessel_min.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_right_femoral_vessel_min.flag').define(Algo.defaultTuneFlag())
    );

  buildAreaMeasurement(
    builder,
    'tp.pl_right_femoral_vessel_min',
    'vessel_wo_vessel_calcification.triangles',
    'world_top_direction',
    undefined,
    'tp.pt_right_femoral_vessel'
  );

  builder
    .name('tp.pl_left_femoral_vessel_min')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder
        .name('tp.pl_left_femoral_vessel_min.dflt')
        .bind(
          Algo.minSlicePlaneAlongPolyline,
          DT.Plane,
          'vessel.smooth_centerline',
          'vessel.smooth_centerline.edge_tree',
          'vessel.smooth_centerline.vertex_diameters',
          'vessel.left_smooth_centerline'
        ),
      builder.name('tp.pl_left_femoral_vessel_min.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_left_femoral_vessel_min.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_left_femoral_vessel_min.flag').define(Algo.defaultTuneFlag())
    );

  buildAreaMeasurement(
    builder,
    'tp.pl_left_femoral_vessel_min',
    'vessel_wo_vessel_calcification.triangles',
    'world_top_direction',
    undefined,
    'tp.pt_left_femoral_vessel'
  );

  builder
    .name('tp.pl_femoral_vessel_custom')
    .bindRaw(
      Algo.tuneValue,
      DT.Plane,
      builder
        .name('tp.pl_femoral_vessel_custom.dflt')
        .bind(
          Algo.slicePlaneAtPoint,
          DT.Plane,
          'vessel.smooth_centerline',
          'vessel.smooth_centerline.edge_tree',
          'tp.pt_right_femoral_vessel'
        ),
      builder.name('tp.pl_femoral_vessel_custom.user').define(Algo.emptyPlane()),
      builder.name('tp.pl_femoral_vessel_custom.appr').define(Algo.emptyPlane()),
      builder.name('tp.pl_femoral_vessel_custom.flag').define(Algo.defaultTuneFlag())
    );

  buildAreaMeasurement(
    builder,
    'tp.pl_femoral_vessel_custom',
    'vessel_wo_vessel_calcification.triangles',
    'world_top_direction'
  );
}
